import React, {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {
    Checkbox,
    Button,
    Dropdown,
    DropdownProps,
    Header,
    Icon,
    Message,
    Segment,
    Table
} from "semantic-ui-react";
import {Link} from "react-router-dom";
import styles from "./index.module.css";
import {
    getTaskResultColor,
    getTaskResultDisplayName,
    getTaskStatusColor,
    getTaskStatusDisplayName,
    TaskModel,
    TaskStatus
} from "../workTableTask/TaskModel";
import {ImportEntityType, ImportSessionResponse, ImportMapping} from "../../services/impl/ImportDataProvider";
import {DoctorOptions} from "../dashboard";
import {
    DOCTOR_TITLE_IM,
    DOCTOR_TITLE_A,
    ENTITY_NAME_A,
    ENTITY_OPTIONS,
    MAX_PREVIEW_ROWS,
    PATIENT_TITLE_OV,
    formatBytes,
    formatDateTime,
    formatPreviewValue
} from "./ImportDataPageHelpers";

interface UploadSectionProps {
    entityType: ImportEntityType;
    file: File | undefined;
    uploading: boolean;
    executing: boolean;
    hasSession: boolean;
    onEntityChange: (_event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => void;
    onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    onUpload: () => void;
    onReset: () => void;
}

interface MappingSectionProps {
    entityType: ImportEntityType;
    session: ImportSessionResponse;
    mapping: ImportMapping;
    mappingOptions: Array<{value: string; text: string; required?: boolean}>;
    mappingError?: string;
    configOptions: Array<{key: string; value: string; text: string}>;
    selectedConfigId: string;
    loadingConfigs: boolean;
    savingConfig: boolean;
    deletingConfig: boolean;
    canSaveCurrentConfig: boolean;
    usedTargets: Record<string, string>;
    onMappingChange: (sourceColumn: string, targetField: string) => void;
    onConfigSelect: (_event: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => void;
    saveCurrentConfig: () => void;
    deleteSelectedConfig: () => void;
}

type MappingOption = {value: string; text: string; required?: boolean; disabled?: boolean};

interface MappingDropdownProps {
    value: string;
    options: MappingOption[];
    onChange: (value: string) => void;
}

const MappingDropdown = ({value, options, onChange}: MappingDropdownProps) => {
    const triggerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const [position, setPosition] = useState({left: 0, top: 0, width: 0, maxHeight: 260});

    const selectedOption = options.find((option) => option.value === value);
    const visibleOptions = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        if (!normalizedSearch) return options;
        return options.filter((option) => option.text.toLowerCase().includes(normalizedSearch));
    }, [options, search]);

    const updatePosition = () => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const belowSpace = window.innerHeight - rect.bottom - 8;
        const aboveSpace = rect.top - 8;
        const openUp = belowSpace < 220 && aboveSpace > belowSpace;
        const maxHeight = Math.max(140, Math.min(300, openUp ? aboveSpace : belowSpace));

        setPosition({
            left: rect.left,
            top: openUp ? Math.max(8, rect.top - maxHeight - 4) : rect.bottom + 4,
            width: rect.width,
            maxHeight
        });
    };

    useEffect(() => {
        if (!open) return;

        updatePosition();
        const handleMouseDown = (event: MouseEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
            setOpen(false);
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };

        document.addEventListener("mousedown", handleMouseDown);
        document.addEventListener("keydown", handleKeyDown);
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);

        return () => {
            document.removeEventListener("mousedown", handleMouseDown);
            document.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
        };
    }, [open]);

    const menu = open ? createPortal(
        <div
            ref={menuRef}
            className={styles.mappingDropdownMenu}
            style={{
                left: position.left,
                top: position.top,
                width: position.width,
                maxHeight: position.maxHeight
            }}
        >
            {/*<div className={styles.mappingDropdownSearch}>*/}
            {/*    <input*/}
            {/*        autoFocus*/}
            {/*        value={search}*/}
            {/*        placeholder="Поиск поля"*/}
            {/*        onChange={(event) => setSearch(event.target.value)}*/}
            {/*    />*/}
            {/*</div>*/}
            <div className={styles.mappingDropdownItems}>
                {visibleOptions.length > 0 ? visibleOptions.map((option) => (
                    <button
                        key={option.value || "__empty__"}
                        type="button"
                        className={[
                            styles.mappingDropdownItem,
                            option.value === value ? styles.mappingDropdownItemSelected : "",
                            option.disabled ? styles.mappingDropdownItemDisabled : ""
                        ].filter(Boolean).join(" ")}
                        disabled={option.disabled}
                        onClick={() => {
                            onChange(option.value);
                            setOpen(false);
                            setSearch("");
                        }}
                    >
                        {option.text}
                    </button>
                )) : (
                    <div className={styles.mappingDropdownEmpty}>Ничего не найдено</div>
                )}
            </div>
        </div>,
        document.body
    ) : null;

    return (
        <>
            <div
                ref={triggerRef}
                tabIndex={0}
                className={`ui fluid selection dropdown ${open ? "active visible" : ""}`}
                onClick={() => {
                    updatePosition();
                    setOpen((current) => !current);
                }}
                onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        updatePosition();
                        setOpen((current) => !current);
                    }
                }}
            >
                <i className="dropdown icon"/>
                <div className={selectedOption ? "text" : "default text"}>
                    {selectedOption?.text || "Выберите поле"}
                </div>
            </div>
            {menu}
        </>
    );
};

interface RunSettingsSectionProps {
    entityType: ImportEntityType;
    isUidMapped: boolean;
    defaultDoctorId: string;
    doctorOptions: DoctorOptions[];
    loadingDoctors: boolean;
    doctorSelectionError?: string;
    skipErrors: boolean;
    detailedLog: boolean;
    executing: boolean;
    canStartImport: boolean;
    onDefaultDoctorChange: (value: string) => void;
    onSkipErrorsChange: (value: boolean) => void;
    onDetailedLogChange: (value: boolean) => void;
    onStart: () => void;
}

interface TaskStatusSectionProps {
    taskId: string;
    task: TaskModel | undefined;
    taskLoading: boolean;
    onRefresh: () => void;
}

export const UploadStepSection = ({
    entityType,
    file,
    uploading,
    executing,
    hasSession,
    onEntityChange,
    onFileChange,
    onUpload,
    onReset
}: UploadSectionProps) => (
    <Segment raised>
        <Header as="h3">1. Выберите тип данных и файл</Header>
        <p>
            Поддерживаются форматы: <b>xlsx</b>, <b>xls</b>, <b>csv</b>, <b>json</b>.
        </p>
        <Dropdown
            selection
            clearable={false}
            options={ENTITY_OPTIONS}
            value={entityType}
            onChange={onEntityChange}
        />
        <div className={styles.section}>
            <input
                id="import-data-file"
                type="file"
                accept=".xlsx,.xls,.csv,.json"
                onChange={onFileChange}
                disabled={uploading || executing}
            />
        </div>
        <div className={styles.section}>
            <Button color="teal" icon loading={uploading} disabled={!file || uploading || executing} onClick={onUpload}>
                <Icon name="upload"/> Загрузить и считать колонки
            </Button>
            {hasSession && <Button onClick={onReset} basic>
                Сбросить
            </Button>}
        </div>
    </Segment>
);

export const MappingSection = ({
    entityType,
    session,
    mapping,
    mappingOptions,
    mappingError,
    configOptions,
    selectedConfigId,
    loadingConfigs,
    savingConfig,
    deletingConfig,
    canSaveCurrentConfig,
    usedTargets,
    onMappingChange,
    onConfigSelect,
    saveCurrentConfig,
    deleteSelectedConfig,
}: MappingSectionProps) => (
    <Segment raised className={styles.section}>
        <Header as="h3">2. Предпросмотр и сопоставление колонок</Header>
        {!!mappingError && <Message warning>{mappingError}</Message>}
        <div className={styles.sessionInfo}>
            <p><b>Файл:</b> {session.fileName} ({formatBytes(session.fileSize)})</p>
            <p><b>Строк в файле:</b> {session.totalRows} | <b>Поля:</b> {session.detectedColumns.length}</p>
        </div>
        <div className={styles.section}>
            <Header as="h4">Настройка сопоставления полей</Header>
            <div className={styles.configActionsRow}>
                <Dropdown
                    selection
                    clearable
                    loading={loadingConfigs}
                    options={[
                        {key: "", value: "", text: "— Не выбрано —"},
                        ...configOptions
                    ]}
                    value={selectedConfigId || ""}
                    placeholder="Выберите конфигурацию"
                    onChange={onConfigSelect}
                />
                <Button
                    basic
                    color="blue"
                    icon="save"
                    loading={savingConfig}
                    disabled={!canSaveCurrentConfig}
                    onClick={saveCurrentConfig}
                >
                    Сохранить конфигурацию
                </Button>
                {!!selectedConfigId && (
                    <Button
                        basic
                        color="red"
                        icon="trash"
                        loading={deletingConfig}
                        disabled={deletingConfig}
                        onClick={deleteSelectedConfig}
                    >
                        Удалить конфигурацию
                    </Button>
                )}
            </div>
        </div>
        {session.detectedColumns.length > 0 ? (
            <div className={styles.previewContainer}>
                <Table celled compact className={styles.mappingTable}>
                    <Table.Header>
                        <Table.Row>
                            {session.detectedColumns.map((sourceColumn) => (
                                <Table.HeaderCell key={`header-${sourceColumn}`}>
                                    {sourceColumn || "Без имени"}
                                </Table.HeaderCell>
                            ))}
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                        <Table.Row>
                            {session.detectedColumns.map((sourceColumn) => {
                                const current = mapping[sourceColumn] || "";
                                return (
                                    <Table.Cell key={`mapping-${sourceColumn}`} className={styles.mappingCell}>
                                        <MappingDropdown
                                            value={current}
                                            options={mappingOptions
                                                .map((target) => ({
                                                    ...target,
                                                    text: target.text + (target.required ? " (обязательное)" : ""),
                                                    disabled:
                                                        !!target.value &&
                                                        !!usedTargets[target.value] &&
                                                        usedTargets[target.value] !== sourceColumn,
                                                }))
                                            }
                                            onChange={(target) => onMappingChange(sourceColumn, target)}
                                        />
                                    </Table.Cell>
                                );
                            })}
                        </Table.Row>
                        {session.sampleRows.slice(0, MAX_PREVIEW_ROWS).map((sampleRow, rowIndex) => (
                            <Table.Row key={`sample-${rowIndex}`}>
                                {session.detectedColumns.map((sourceColumn) => (
                                    <Table.Cell key={`${sourceColumn}-${rowIndex}`} className={styles.previewCell}>
                                        {formatPreviewValue(entityType, sourceColumn, sampleRow[sourceColumn], mapping[sourceColumn])}
                                    </Table.Cell>
                                ))}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </div>
        ) : (
            <Message warning>Файл не содержит данных или не удалось распознать заголовок.</Message>
        )}
    </Segment>
);

export const RunSettingsSection = ({
    entityType,
    isUidMapped,
    defaultDoctorId,
    doctorOptions,
    loadingDoctors,
    doctorSelectionError,
    skipErrors,
    detailedLog,
    executing,
    canStartImport,
    onDefaultDoctorChange,
    onSkipErrorsChange,
    onDetailedLogChange,
    onStart
}: RunSettingsSectionProps) => (
    <Segment raised className={styles.section}>
        <Header as="h3">3. Настройки запуска</Header>
        {!isUidMapped && (
            <Message warning>
                Внимание: UID не указан. Поиск существующего {ENTITY_NAME_A[entityType]} будет выполнен по сочетанию
                <b> {entityType === "patient" ? "ФИО + телефон + дата рождения" : "ФИО + телефон"}</b>. Убедитесь,
                что эти поля заполнены в источнике, чтобы избежать дубликатов.
            </Message>
        )}
        {entityType === "patient" && (
            <>
                <Message info>
                    {DOCTOR_TITLE_IM} по умолчанию всегда используется как резервный источник. Его значение применяется,
                    если в строке не указан/не найден сопоставленный <b>«{DOCTOR_TITLE_IM}»</b>.
                </Message>
                <div className={styles.optionSelect}>
                    <label>{DOCTOR_TITLE_IM} по умолчанию для новых {PATIENT_TITLE_OV}</label>
                    <Dropdown
                        fluid
                        search
                        selection
                        loading={loadingDoctors}
                        value={defaultDoctorId}
                        options={doctorOptions}
                        placeholder={`Выберите ${DOCTOR_TITLE_A}`}
                        onChange={(_event, data) => onDefaultDoctorChange((data.value || "") as string)}
                    />
                </div>
                {!!doctorSelectionError && <Message warning>{doctorSelectionError}</Message>}
            </>
        )}
        <div className={styles.optionsGrid}>
            <Checkbox
                label="Пропускать ошибки и продолжать импорт"
                checked={skipErrors}
                onChange={(_event, data) => onSkipErrorsChange(!!data.checked)}
            />
            <Checkbox
                label="Подробный журнал загрузки"
                checked={detailedLog}
                onChange={(_event, data) => onDetailedLogChange(!!data.checked)}
            />
        </div>
        <Button color="blue" icon loading={executing} disabled={!canStartImport || executing} onClick={onStart}>
            <Icon name="play"/> Запустить импорт в очередь
        </Button>
    </Segment>
);

export const TaskStatusSection = ({
    taskId,
    task,
    taskLoading,
    onRefresh,
}: TaskStatusSectionProps) => {
    const taskResultText = task?.result ? getTaskResultDisplayName(task.result) : "—";
    const taskResultColor = task?.result ? getTaskResultColor(task.result) : undefined;
    const taskStatusText = task?.status ? getTaskStatusDisplayName(task.status) : "—";
    const taskStatusColor = task?.status ? getTaskStatusColor(task.status) : undefined;

    return (
        <Segment raised className={styles.section}>
            <Header as="h3">4. Статус задачи импорта</Header>
            <p>Задача ID: <b>{taskId}</b></p>
            <Button icon onClick={onRefresh} loading={taskLoading} disabled={taskLoading}>
                <Icon name="refresh"/> Обновить статус
            </Button>
            {task ? (
                <div className={styles.taskInfo}>
                    <p><b>Статус:</b> <span style={{color: taskStatusColor}}>{taskStatusText}</span></p>
                    <p><b>Результат:</b> <span style={{color: taskResultColor}}>{taskResultText}</span></p>
                    <p><b>Создана:</b> {formatDateTime(task.created)}</p>
                    <p><b>Запущена:</b> {formatDateTime(task.startDate)}</p>
                    <p><b>Завершена:</b> {formatDateTime(task.endDate)}</p>
                    {!!task.comment && <p><b>Комментарий:</b> {task.comment}</p>}
                </div>
            ) : <Message info>Загрузка статуса...</Message>}
            {!!task?.error && <Message negative>{task.error}</Message>}
            <div className={styles.taskLogWrap}>
                <p><b>Лог</b></p>
                <pre className={styles.taskLogBlock}>
                    {task?.log ?? "Лог еще не сформирован."}
                </pre>
            </div>
            <div className={styles.section}>
                <Button as={Link} to={"/dashboard/tasks"} color="green">
                    Перейти к задачам
                </Button>
            </div>
        </Segment>
    );
};
